/** Écarts mot à mot entre deux textes (comparaison « Votre texte » / « Texte généré à jour », docs/18 §6.7) :
 *  plus longue sous-suite commune sur les mots, après retrait du début et de la fin communs. Fonctions pures. */

export interface DiffPart {
  text: string;
  kind: "same" | "del" | "add";
}

/** Au-delà de ce nombre de cases (mots × mots), le milieu est rendu en un seul bloc retiré puis ajouté. */
const MAX_CELLS = 1_500_000;

/** Mots et blancs, dans l'ordre (les blancs restent attachés comme jetons à part). */
export function tokens(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) ?? [];
}

function push(out: DiffPart[], kind: DiffPart["kind"], text: string): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && last.kind === kind) last.text += text;
  else out.push({ kind, text });
}

/** Écarts de `before` à `after` : parties communes, retirées (`del`) et ajoutées (`add`), contiguës fusionnées. */
export function diffWords(before: string, after: string): DiffPart[] {
  const a = tokens(before);
  const b = tokens(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const out: DiffPart[] = [];
  push(out, "same", a.slice(0, start).join(""));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  if (midA.length * midB.length > MAX_CELLS) {
    push(out, "del", midA.join(""));
    push(out, "add", midB.join(""));
  } else {
    // Table des longueurs de la plus longue sous-suite commune, du bas vers le haut.
    const n = midA.length;
    const m = midB.length;
    const table: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        table[i]![j] = midA[i] === midB[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (midA[i] === midB[j]) {
        push(out, "same", midA[i]!);
        i++;
        j++;
      } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
        push(out, "del", midA[i]!);
        i++;
      } else {
        push(out, "add", midB[j]!);
        j++;
      }
    }
    while (i < n) push(out, "del", midA[i++]!);
    while (j < m) push(out, "add", midB[j++]!);
  }
  push(out, "same", a.slice(endA).join(""));
  return out;
}

/** Parties d'un côté de la comparaison : « Votre texte » garde les parties communes et retirées, « Texte généré à
 *  jour » les communes et ajoutées. */
export function sideParts(parts: readonly DiffPart[], side: "before" | "after"): DiffPart[] {
  return parts.filter((p) => p.kind === "same" || p.kind === (side === "before" ? "del" : "add"));
}
